/**
 * Optimizer Control Center — scheduler status of every Flow Optimizer tracker
 * For Media Devoted | Created By Ciro Autiero
 *
 * Only the all-trackers kill switch is read here (the red banner). A
 * tracker's own switch (Tres Commas `blockedReason`) is never ORed in: it is
 * the amber TrackerHeldNotice, read through useTrackerBlockedReason (same
 * query key, so the request is shared).
 */

import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import type { FlowOptimizerSchedulerStatus } from '@trafficpulse/shared';

import { getSchedulerStatus } from '@/features/flow-optimizer/services/flow-optimizer-api';
import { optimizerCenterKeys } from '@/features/optimizer-center/hooks/useOptimizerConfigs';
import { useFlowOptimizerTrackers } from '@/features/trackers/flow-optimizer/useFlowOptimizerTrackers';

export interface UseOptimizerSchedulerStatusResult {
  /** True when any tracker reports the FLOW_OPTIMIZER_DISABLED gate. */
  globalKillSwitch: boolean;
}

function readGlobalSwitch(
  results: UseQueryResult<FlowOptimizerSchedulerStatus>[],
): UseOptimizerSchedulerStatusResult {
  return { globalKillSwitch: results.some((r) => Boolean(r.data?.globalKillSwitch)) };
}

export function useOptimizerSchedulerStatus(): UseOptimizerSchedulerStatusResult {
  const trackers = useFlowOptimizerTrackers();
  return useQueries({
    queries: trackers.map((provider) => ({
      queryKey: optimizerCenterKeys.scheduler(provider),
      queryFn: () => getSchedulerStatus(provider),
      refetchInterval: 60_000,
    })),
    combine: readGlobalSwitch,
  });
}
